import React, {
  FC,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useOnClickOutside from 'use-onclickoutside'
import { useSearchForm } from './hooks'
import styles from './styles.module.scss'
import clsx from 'clsx'

interface Item {
  value: string
  label: string
  icon?: FC
}

interface Props {
  className?: string
  targetValue: string
  selectList: Array<Item>
  label: string
  labelInside: string
  withWhiteArrow?: boolean
  withInternalScroll?: boolean
  withSearch?: boolean
  onChangeAction: (value: string) => void
  needContentArr?: string[]
  renderContent: () => ReactNode
}

export const SelectFilters: FC<Props> = allProps => {
  const {
    label,
    selectList,
    targetValue,
    labelInside,
    needContentArr,
    renderContent,
    withSearch,
    withWhiteArrow,
    withInternalScroll,
    onChangeAction,
    ...props
  } = allProps

  const [active, setActive] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)
  const close = useCallback(() => setActive(false), [])
  const toggle = useCallback(() => setActive(f => !f), [])

  const onChange = useCallback(
    (item: Item) => () => {
      onChangeAction(item.value)
      close()
    },
    [onChangeAction, close]
  )

  useOnClickOutside(ref, close)

  const selectedItem = useMemo(
    () => selectList.find(({ value }) => value === targetValue),
    [selectList, targetValue]
  )

  const form = useSearchForm(selectList, selectedItem, onChange)

  const SelectedIcon = useMemo(
    () => selectedItem && selectedItem.icon,
    [selectedItem]
  )

  useLayoutEffect(() => {
    if (withSearch && active && searchRef.current) {
      searchRef.current.focus()
    }
  }, [active, withSearch])

  return (
    <div {...props} className={clsx(styles.root, props.className)} ref={ref}>
      <div className={clsx('SelectFilters', 'unselectable')}>
        {active && withSearch ? (
          <form onSubmit={form.submit} onKeyDown={form.keyDown}>
            <input
              type='string'
              onChange={form.change}
              value={form.value}
              ref={searchRef}
            />
            {/* <IconGrey /> */}
          </form>
        ) : (
          label && (
            <label className='SelectFilters__Label' onClick={close}>
              {label}
            </label>
          )
        )}

        <div
          className={`SelectFilters__Current ${'Current__' + props.className}`}
          onClick={toggle}
        >
          {SelectedIcon && (
            <div className='SelectFilters__Item-Icon'>
              <SelectedIcon />
            </div>
          )}
          {(!withSearch || !active) && (
            <div className='SelectFilters__CurrentLabelContent'>
              <div className='SelectFilters__CurrentLabelText'>
                {labelInside}
                {selectedItem && selectedItem.label}
              </div>
              <div
                className={clsx('SelectFilters__SelectIcon', {
                  'SelectFilters__SelectIcon--withWhiteArrow': withWhiteArrow,
                  'SelectFilters__SelectIcon--active': active,
                })}
              />
            </div>
          )}
        </div>

        <div
          style={{
            overflowY: withSearch
              ? 'scroll'
              : withInternalScroll && active
              ? 'auto'
              : 'hidden',
          }}
          className={clsx('SelectFilters__Picker', {
            'SelectFilters__Picker--active': active,
          })}
        >
          <div className='SelectFilters__Picker-Inner'>
            {form.filtered.map((item, i) => {
              const { value, label, icon: Icon } = item
              const active = selectedItem && selectedItem.value === value

              if (active && !withSearch) return

              return (
                <div
                  key={value}
                  className={clsx('SelectFilters__Item', {
                    'SelectFilters__Item--active': form.focused === i,
                  })}
                  onClick={active ? close : onChange(item)}
                >
                  {Icon && (
                    <div className='SelectFilters__Item-Icon'>
                      <Icon />
                    </div>
                  )}
                  <div
                    className={`SelectFilters__Item-Label ${
                      'Item-Label__' + props.className
                    }`}
                  >
                    {label}
                  </div>
                </div>
              )
            })}
            {!form.filtered.length && selectList.length && (
              <div className='SelectFilters__Item'>
                <div
                  className={`SelectFilters__Item-Label ${
                    'Item-Label__' + props.className
                  }`}
                >
                  No items found
                </div>
              </div>
            )}
          </div>
        </div>

        {!needContentArr ||
          (Array.isArray(needContentArr) &&
            needContentArr.indexOf(
              (selectedItem && selectedItem.value) as string
            ) !== -1 &&
            renderContent())}
      </div>
    </div>
  )
}
